import { ConfigService } from '@stockcare-services/config-service/config.service';
import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { IPatient } from '@stockcare-models/IPatient';
import { PerformanceMetricService } from '@stockcare-services/performance-metric-service/performance-metric-service';
import { Observable } from 'rxjs';
import { AnalyticsNames } from '@stockcare-helpers/analytics-names';
import { IPatientSummary } from '@stockcare-models/PatientSummary';
import { PatientStorageService } from '@stockcare-storage/patient/patient-storage.service';
import { IPatientTransfer } from '@stockcare-models/IPatientTransfer';
import { IKeyValue } from '@stockcare-models/IKeyValue';
import { PatientDischargeDto } from '@stockcare-models/PatientDischargeDto';
import { Patient } from '@stockcare-storage/patient/patient';
import { IDoctor } from '@stockcare-models/IDoctor';
import { take } from 'rxjs/operators';
import { PatientNotes } from '@stockcare-models/IPatientNotes';
import { QueueType } from '@stockcare-models/QueueType';
import { QueueService } from '@stockcare-services/queue-service/queue.service';
import { PatientNoteItem } from '@stockcare-models/PatientNoteItem';

@Injectable({
  providedIn: 'root',
})
export class PatientService {
  constructor(
    private http: HttpClient,
    private conf: ConfigService,
    private performanceMetricService: PerformanceMetricService,
    private patientStorageService: PatientStorageService,
    private queueService: QueueService,
  ) {}

  getPatient(caseNumber: string, institutionId: number) {
    return this.http.get<IPatient>(`${this.conf.config.baseUrl}/patients/casenumber/${caseNumber}/${institutionId}`);
  }

  getPatientSummary(caseNumber: string) {
    return this.http.get<IPatientSummary>(`${this.conf.config.baseUrl}/patients/GetPatientSummary/${caseNumber}`);
  }

  getFromWard(id: number): Observable<IPatient[]> {
    const startTime = new Date();
    return new Observable((observer) => {
      this.http.get<IPatient[]>(`${this.conf.config.baseUrl}/patients/ward/${id}`).subscribe(
        (result) => {
          this.savePatients(result, observer, startTime);
        },
        (error) => observer.error(error)
      );
    });
  }

  getMovedFromWard(wardId: number): Observable<IPatient[]> {
    const startTime = new Date();
    return new Observable((observer) => {
      this.http
        .get<IPatient[]>(`${this.conf.config.baseUrl}/patients/ward/moved/${wardId}`)
        .pipe(take(1))
        .subscribe(
          (result) => {
            this.savePatients(result, observer, startTime);
          },
          (error) => observer.error(error)
        );
    });
  }

  getWhiteboardFromWard(id: number): Observable<IPatient[]> {
    const startTime = new Date();
    return new Observable((observer) => {
      this.http
        .get<IPatient[]>(`${this.conf.config.baseUrl}/patients/whiteboard/ward/${id}`)
        .pipe(take(1))
        .subscribe(
          (result) => {
            this.savePatients(result, observer, startTime);
          },
          (error) => observer.error(error)
        );
    });
  }

  transferPatient(patientTransfer: IPatientTransfer): Observable<any> {
    return this.http.post<IPatientTransfer>(`${this.conf.config.baseUrl}/patients/transfer/`, patientTransfer);
  }

  private savePatients(result: IPatient[], observer, startTime: Date) {
    this.addUpdatePatientDb(result).then(() => {
      observer.next(result);
    });
    this.performanceMetricService.buildAndSubmitMetrics(startTime, AnalyticsNames.patientListQuery);
  }

  getTheatreHistoryPatients(physicalLocationId: number): Observable<IPatient[]> {
    return this.http.get<IPatient[]>(`${this.conf.config.baseUrl}/ward/${physicalLocationId}`);
  }

  private async addUpdatePatientDb(patients: IPatient[]) {
    const mappedPatients = this.patientStorageService.map(patients);
    await this.patientStorageService.deleteAll();

    if (mappedPatients && mappedPatients.length > 0) {
      await this.patientStorageService.addPatients(mappedPatients);
    }
  }

  clearPatientDischargeFlag(patientId: number) {
    return this.http.post<IPatientSummary>(`${this.conf.config.baseUrl}/patients/clearPatientDischargeFlag/${patientId}`, null);
  }

  dischargePatient(patientDischargeDto: PatientDischargeDto) {
    return this.http.post<PatientDischargeDto>(`${this.conf.config.baseUrl}/patients/discharge`, patientDischargeDto);
  }

  getDischargeReasons(): Observable<IKeyValue[]> {
    return this.http.get<IKeyValue[]>(`${this.conf.config.baseUrl}/patients/GetDischargeReasons`);
  }

  getPatientNotes(patientId: number): Observable<PatientNotes[]> {
    return this.http.post<PatientNotes[]>(`${this.conf.config.baseUrl}/patients/getPatientNotes/${patientId}`, null);
  }

  createPatientNote(patientNote: PatientNoteItem): Observable<any> {
    return new Observable((observer) => {
      this.queueService.enqueue(QueueType.PatientNoteCreatedQueue, patientNote).subscribe(
        () => {
          observer.next(true);
        },
        (error) => {
          observer.error(false);
        }
      );
    });
  }

  toIPatient(patient: Patient): IPatient {
    let doctor: IDoctor = {
      name: patient.AttendingPhysicianName,
      id: patient.AttendingPhysicianId,
      telephoneNumber: patient.AttendingPhysicianTelephoneNumber,
      title: patient.AttendingPhysicianTitle,
      city: '',
      createdByUserId: '',
      createdTime: null,
      postalCode: '',
      practiceNumber: '',
      referenceId: '',
      updatedByUserId: '',
      updatedTime: null,
    };

    const outputPatient: IPatient = {
      admittedDate: patient.AdmittedDate,
      bed: patient.Bed,
      caseNumber: patient.CaseNumber,
      department: patient.Department,
      discharged: patient.Discharged,
      dischargedDate: patient.DischargedDate,
      id: patient.Id,
      name: patient.Name,
      room: patient.Room,
      nursingOrganizationUnit: patient.NursingOrganizationUnit,
      patientStatus: patient.PatientStatus,
      physicalLocationId: patient.PhysicalLocationId,
      referenceId: patient.ReferenceId,
      attendingPhysician: doctor,
      createdByUserId: patient.CreatedByUserId,
      createdTime: patient.CreatedTime,
      updatedByUserId: patient.UpdatedByUserId,
      updatedTime: patient.UpdatedTime,
      medicalAidDescription: patient.MedicalAidDescription,
      canBeMovedToIcu: patient.CanBeMovedToICU,
      lastScored: patient.LastScored,
      lastScore: patient.LastScore
    };

    return outputPatient;
  }
}
